import type { IncomingCallView } from "@vaulted/shared";
import { callerIdName, formatPhone } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { errorMessage, get, post } from "../api";
import { ClientPicker, NumberActions } from "../pages/Voicemail";
import { useSession } from "../session";
import { GfeBadge } from "./Gfe";
import { Alert, Badge, formatDateTime } from "./ui";

/**
 * Who's calling: cards in the corner for calls ringing, in progress, or over (each stays until you
 * close it; ended calls drop off by themselves 15 minutes later, an unknown caller someone answered
 * once they're sorted out). The newest is open; older ones fold to a line (click to open), three at most.
 */
export function IncomingCalls() {
  const { can } = useSession();
  const allowed = can("calls.view", "appointments.manage");
  const calls = useQuery({ queryKey: ["calls-ringing"], queryFn: () => get<IncomingCallView[]>("/api/calls/ringing"), enabled: allowed, refetchInterval: 3_000 });
  const [closed, setClosed] = useState<string[]>(readClosed);
  const [open, setOpen] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const showing = (calls.data ?? []).filter((c) => !closed.includes(c.id));
  if (!allowed || !showing.length) return null;
  const close = (id: string) => {
    const next = [...closed, id].slice(-50);
    setClosed(next);
    saveClosed(next);
  };
  const expanded = showing.some((c) => c.id === open) ? open : showing[0]!.id;
  const visible = all ? showing : showing.slice(0, 3);
  return (
    <div className="fixed right-4 bottom-4 z-30 flex max-h-[calc(100dvh-2rem)] w-[min(380px,calc(100vw-2rem))] flex-col gap-2 overflow-y-auto" role="status" aria-live="polite">
      {showing.length > 1 && (
        <button className="self-end rounded-full bg-white px-3 py-1 text-xs font-semibold text-ink-soft shadow hover:text-ink" onClick={() => showing.forEach((c) => close(c.id))}>
          Close all
        </button>
      )}
      {visible.map((c) =>
        c.id === expanded ? (
          <CallCard key={c.id} c={c} onClose={() => close(c.id)} />
        ) : (
          <CallLine key={c.id} c={c} onOpen={() => setOpen(c.id)} onClose={() => close(c.id)} />
        ),
      )}
      {showing.length > visible.length && (
        <button className="rounded-[var(--radius-control)] bg-white px-3 py-2 text-left text-xs font-semibold text-brand shadow" onClick={() => setAll(true)}>
          +{showing.length - visible.length} more
        </button>
      )}
    </div>
  );
}

// Closed cards stay closed on this computer (a page reload doesn't bring them back).
const CLOSED_KEY = "vaulted.closedCalls";
function readClosed(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(CLOSED_KEY) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function saveClosed(ids: string[]) {
  try {
    localStorage.setItem(CLOSED_KEY, JSON.stringify(ids));
  } catch {
    // Not saved; it only comes back on a reload.
  }
}

const clock = (iso: string) => new Intl.DateTimeFormat("en-US", { timeStyle: "short" }).format(new Date(iso));

/** What's happening with the call, in a few words. */
function stateLabel(c: IncomingCallView) {
  if (c.status === "ringing") return "Incoming call";
  if (c.status === "answered") return "On a call";
  if (!c.answered) return `Missed call, ${clock(c.endedAt ?? c.startedAt)}`;
  return !c.callers.length && !c.knownName ? "Call ended: who was it?" : `Call ended ${clock(c.endedAt ?? c.startedAt)}`;
}

/** Who it is, in a few words. */
function whoLabel(c: IncomingCallView) {
  if (c.callers.length) return c.callers.map((p) => p.name).join(", ");
  if (c.knownName) return c.knownName;
  return c.callerName ? callerIdLabel(c.callerName) : formatPhone(c.from);
}

/** An older call, folded to one line. */
function CallLine({ c, onOpen, onClose }: { c: IncomingCallView; onOpen: () => void; onClose: () => void }) {
  return (
    <div className="flex items-center gap-2 rounded-[var(--radius-control)] border border-mist bg-white px-3 py-2 text-sm shadow">
      <button className="min-w-0 flex-1 truncate text-left" onClick={onOpen} title="Show this call">
        <span className="font-semibold">{whoLabel(c)}</span>
        <span className="text-muted"> · {stateLabel(c)}</span>
      </button>
      <button className="text-muted hover:text-ink" onClick={onClose} aria-label="Close">
        ✕
      </button>
    </div>
  );
}

function CallCard({ c, onClose }: { c: IncomingCallView; onClose: () => void }) {
  // A known client's name leads; the number moves to the small line under it.
  const when = (
    <p className="text-xs text-muted">
      {c.callers.length > 0 && `${formatPhone(c.from)} · `}
      {formatDateTime(c.startedAt)}
      {c.answeredBy && ` · Answered by ${c.answeredBy}`}
    </p>
  );
  return (
    <div className="rounded-[var(--radius-panel)] border border-mist bg-white p-4 text-sm shadow-lg">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold tracking-wide text-muted uppercase">{stateLabel(c)}</p>
          {c.callers.length ? (
            <ul className="mt-1 flex flex-col gap-3">
              {c.callers.map((p, i) => (
                <li key={p.id} className="flex flex-col gap-1 border-t border-mist pt-2 first:border-0 first:pt-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/clients/${p.id}`} className="text-lg leading-tight font-semibold text-brand underline">
                      {p.name}
                    </Link>
                    {p.minor && <Badge tone="wait">Under 18</Badge>}
                    {p.tags.map((t) => (
                      <span key={t.name} className="rounded-full px-2 py-0.5 text-xs font-semibold text-white" style={{ background: t.color }}>
                        {t.name}
                      </span>
                    ))}
                  </div>
                  {i === 0 && when}
                  {p.nextVisit ? (
                    <p className="text-ink-soft">
                      Next: {p.nextVisit.serviceName}, {formatDateTime(p.nextVisit.start)}
                      {p.nextVisit.status === "unconfirmed" && <span className="text-wait"> (not confirmed)</span>}
                    </p>
                  ) : (
                    <p className="text-muted">No upcoming visit.</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <GfeBadge g={p.gfe} compact />
                    {p.formsNeeded > 0 && (
                      <span className="text-xs font-semibold text-wait">
                        {p.formsNeeded} form{p.formsNeeded === 1 ? "" : "s"} to sign
                      </span>
                    )}
                  </div>
                  {p.lastText && (
                    <p className="truncate text-xs text-muted" title={p.lastText.body}>
                      Last text {formatDateTime(p.lastText.at)}: “{p.lastText.body}”
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <>
              <p className="text-base font-semibold">{formatPhone(c.from)}</p>
              {!c.knownName && c.callerName && <p className="text-ink-soft">Caller ID: {callerIdLabel(c.callerName)}</p>}
              {c.knownName && <p className="text-ink-soft">{c.knownName} (not a client)</p>}
              {when}
            </>
          )}
        </div>
        <button className="text-muted hover:text-ink" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      {!c.callers.length && !c.knownName && <Unknown c={c} />}
    </div>
  );
}

/** A caller nobody knows: add them, say which client it was, or mark it not a client or spam. */
function Unknown({ c }: { c: IncomingCallView }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const [linking, setLinking] = useState(false);
  const link = useMutation({
    mutationFn: (clientId: string) => post(`/api/clients/${clientId}/phones`, { phone: c.from, label: "" }),
    onSuccess: () => Promise.all(["calls-ringing", "calls", "comm", "client-phones"].map((k) => qc.invalidateQueries({ queryKey: [k] }))),
  });
  return (
    <div className="mt-2 flex flex-col gap-2">
      <p className="text-ink-soft">No client has this number.</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {can("clients.edit") && (
          <Link to={addClientLink(c.from, c.callerName)} className="font-semibold text-brand underline">
            Add client
          </Link>
        )}
        {can("clients.edit") && (
          <button className="font-semibold text-brand underline" onClick={() => setLinking(!linking)}>
            {linking ? "Close" : "Link to a client"}
          </button>
        )}
      </div>
      {linking && <ClientPicker onPick={(id) => link.mutate(id)} />}
      {link.error && <Alert>{errorMessage(link.error)}</Alert>}
      <NumberActions phone={c.from} />
    </div>
  );
}

/** Caller ID as shown: a name tidied up ("SMITH JOHN" → "Smith John"), anything else as sent. */
export function callerIdLabel(raw: string) {
  const n = callerIdName(raw);
  return n ? `${n.firstName} ${n.lastName}` : raw;
}

/** The Add client form, with the number and (as a suggestion) the caller ID name filled in. */
export const addClientLink = (phone: string, callerName: string | null) => `/clients?${new URLSearchParams({ add: phone, ...(callerName ? { name: callerName } : {}) })}`;
